import React, {useEffect, useState} from 'react'
import LoaderInline from '../common/LoaderInline'
import {formatAmount, formatDateString, sortBy} from '../../services/utils'
import {Payment} from '../../types/app-types'
import classNames from 'classnames'

interface PaymentListProps {
  payments: Payment[]
  isLoading: boolean
}

interface PaginationState {
  enabled: boolean
  currentPage: number
  pageCount: number
  perPage: number
  hasPrevPage: boolean
  hasNextPage: boolean
}

const initState: PaginationState = {
  enabled: false,
  currentPage: 0,
  hasPrevPage: false,
  hasNextPage: false,
  perPage: 0,
  pageCount: 0
}

function getPaginationState(
  payments: Payment[],
  page: number = 1
): PaginationState {
  const enabled = payments.length > 0
  const perPage = 10
  const pageCount = Math.ceil(payments.length / perPage)
  return {
    currentPage: page,
    hasPrevPage: page > 1,
    hasNextPage: page < pageCount,
    enabled,
    perPage,
    pageCount
  }
}

function formatRefundPayments(payments: Payment[]): Payment[] {
  return payments.map(payment => {
    const amount = payment.amount?.amount || 0
    if (amount >= 0) {
      return payment
    } else {
      const receipt = `Refund: ${payment.receipt}`
      return {...payment, receipt}
    }
  })
}

function getPaymentsPage(
  allPayments: Payment[],
  {currentPage, perPage, enabled}: PaginationState
): Payment[] {
  const payments = formatRefundPayments(allPayments)

  if (enabled) {
    const startIndex = (currentPage - 1) * perPage
    const endIndex = currentPage * perPage
    return payments.slice(startIndex, endIndex)
  } else {
    return payments
  }
}

const PaymentList = (props: PaymentListProps) => {
  const {isLoading} = props
  const payments = props.payments?.length
    ? sortBy(props.payments, 'paymentDate')
    : []
  const [pagination, updatePagination] = useState<PaginationState>(initState)
  const paymentsPage = getPaymentsPage(payments, pagination)

  useEffect(() => {
    updatePagination(getPaginationState(payments))
  }, [payments.length])

  return (
    <div className="mb-4">
      {pagination.enabled ? (
        <div className="btn-group">
          <button
            className={classNames('btn btn-sm btn-outline btn-primary', {
              'btn-disabled': !pagination.hasPrevPage
            })}
            onClick={() => {
              updatePagination(
                getPaginationState(payments, pagination.currentPage - 1)
              )
            }}
          >
            «
          </button>
          <button className="btn btn-sm btn-outline btn-primary">
            Page {pagination.currentPage}
          </button>
          <button
            className={classNames('btn btn-sm btn-outline btn-primary', {
              'btn-disabled': !pagination.hasNextPage
            })}
            onClick={() => {
              updatePagination(
                getPaginationState(payments, pagination.currentPage + 1)
              )
            }}
          >
            »
          </button>
        </div>
      ) : (
        ''
      )}
      <table className="w-full table table-zebra table-compact mt-2">
        <thead>
          <tr>
            <th>Description</th>
            <th>
              <div className="text-right w-full md:w-1/2 mx-auto">Amount</div>
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={2}>
                <LoaderInline text="Loading payments..." />
              </td>
            </tr>
          ) : !isLoading && !payments?.length ? (
            <tr>
              <td colSpan={2}>No Payments</td>
            </tr>
          ) : (
            paymentsPage.map(payment => {
              const {id, method, amount, receipt, paymentDate} = payment
              return (
                <tr key={id}>
                  <td>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs">
                        {formatDateString(paymentDate)} - {method}
                      </span>
                      <span className="truncate w-52 md:w-full">{receipt}</span>
                    </div>
                  </td>
                  <td>
                    <div className="text-right w-full md:w-1/2 mx-auto">
                      {formatAmount(amount.amount)}
                    </div>
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}

export default PaymentList
